export type ServiceIdentifier = string | symbol

type Binding<T> = { type: 'function-component'; value: T } | { type: 'factory'; factory: (container: Container) => T }

export type BindFunction = <T>(key: ServiceIdentifier) => {
  to(value: T): void
  toFactory(factory: (container: Container) => T): void
}

export class ContainerModule {
  constructor(public readonly registry: (bind: BindFunction) => void) {}
}

export class Container {
  private bindings = new Map<ServiceIdentifier, Binding<any>>()
  private instances = new Map<ServiceIdentifier, any>()

  bind: BindFunction = <T>(key: ServiceIdentifier) => {
    return {
      to: (value: T) => {
        this.bindings.set(key, { type: 'function-component', value })
      },
      toFactory: (factory: (container: Container) => T) => {
        this.bindings.set(key, { type: 'factory', factory })
      },
    }
  }

  get<T>(key: ServiceIdentifier): T {
    if (this.instances.has(key)) return this.instances.get(key)

    const binding = this.bindings.get(key)
    if (!binding) throw new Error(`No binding found for key: ${key.toString()}`)

    let instance: T

    switch (binding.type) {
      case 'function-component':
        instance = binding.value
        break
      case 'factory':
        instance = binding.factory(this)
        break
    }

    this.instances.set(key, instance)
    return instance
  }

  load(...modules: ContainerModule[]) {
    for (const module of modules) {
      module.registry(this.bind)
    }
  }
}
